"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleCheck as CheckCircle2, Award, Star } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
interface TrustPoint {
  id: string;
  title: string;
  subtitle: string;
  tag: string;
  iconType: "nasc" | "chas" | "experience" | "google";
}
const trustData: TrustPoint[] = [{
  id: "nasc",
  title: "NASC Accredited",
  subtitle: "National Access & Scaffolding Confederation full member standards",
  tag: "Safety Standard",
  iconType: "nasc"
}, {
  id: "chas",
  title: "CHAS Accredited",
  subtitle: "Demonstrated health, safety, and compliance management",
  tag: "Health & Safety",
  iconType: "chas"
}, {
  id: "experience",
  title: "20+ Years' Experience",
  subtitle: "Hands-on operational expertise across London and South East sites",
  tag: "Proven Track Record",
  iconType: "experience"
}, {
  id: "google",
  title: "5-Star Google Rating",
  subtitle: "Direct feedback from repeat domestic and commercial clients",
  tag: "Client Feedback",
  iconType: "google"
}];
export default function TrustStrip() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (type: TrustPoint["iconType"]) => {
    switch (type) {
      case "nasc":
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
      case "chas":
        return <CheckCircle2 className="size-6 text-primary" aria-hidden="true" />;
      case "experience":
        return <Award className="size-6 text-primary" aria-hidden="true" />;
      case "google":
        return <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
            <Star className="size-4 fill-primary text-primary" />
            <Star className="size-4 fill-primary text-primary" />
            <Star className="size-4 fill-primary text-primary" />
            <Star className="size-4 fill-primary text-primary" />
            <Star className="size-4 fill-primary text-primary" />
          </div>;
      default:
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="truststrip" data-nav="light" className="relative w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header & Intro */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center mb-4">
            <Badge variant="outline" className="bg-card text-foreground font-semibold px-3 py-1 border-border shadow-xs">
              Reliability & Standards
            </Badge>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Built on repeat business and recommendation
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
            BMF Scaffolding works with homeowners, roofing contractors, developers and commercial
            clients throughout London and the South East. Much of our work comes from customers who
            use us repeatedly and recommend us to others.
          </p>
        </motion.div>

        {/* 4-Column Trust Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustData.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
          y: -3
        }} className="h-full">
              <Card className="relative h-full bg-card border border-border rounded-lg shadow-xs hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] duration-200 overflow-hidden group">
                {/* Subtle top primary datum accent line */}
                <div className="absolute top-0 inset-x-0 h-1 bg-muted group-hover:bg-primary transition-colors duration-200" />

                <CardContent className="pt-6 pb-6 px-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="size-11 rounded-md bg-accent flex items-center justify-center border border-border group-hover:border-primary/30 transition-[border-color] duration-200">
                        {renderIcon(item.iconType)}
                      </div>
                      <span className="text-2xs uppercase tracking-wider font-semibold text-muted-foreground px-2 py-0.5 rounded bg-muted/60">
                        {item.tag}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-card-foreground tracking-tight group-hover:text-primary transition-colors duration-200">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-sm leading-normal text-muted-foreground">
                      {item.subtitle}
                    </p>
                  </div>

                  <div className="mt-6 pt-3 border-t border-border/60 flex items-center justify-between text-xs font-medium text-muted-foreground">
                    <span>Verified & active</span>
                    <span className="inline-block size-1.5 rounded-full bg-primary" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}